import { api } from "./api"
import type {
  SurveyTemplateType,
  QuestionType,
  SurveyAssignmentStatus,
  SurveyTemplateListItem,
  SurveyTemplateDetail,
  SurveyQuestionDetail,
  SurveyAssignmentListItem,
  CreateSurveyTemplateRequest,
  UpdateSurveyTemplateRequest,
  CreateSurveyQuestionRequest,
  UpdateSurveyQuestionRequest,
  CreateSurveyAssignmentRequest,
  PublicSurveyInfo,
  SubmitSurveyRequest,
  PaginatedResponse,
} from "@/types/api"

// ===========================================
// Reference Types
// ===========================================

export function getSurveyTemplateTypes(): Promise<SurveyTemplateType[]> {
  return api.get<SurveyTemplateType[]>("/survey-template-types")
}

export function getQuestionTypes(): Promise<QuestionType[]> {
  return api.get<QuestionType[]>("/survey-question-types")
}

export function getSurveyAssignmentStatuses(): Promise<SurveyAssignmentStatus[]> {
  return api.get<SurveyAssignmentStatus[]>("/survey-assignment-statuses")
}

// ===========================================
// Survey Templates
// ===========================================

export interface ListSurveyTemplatesParams {
  page?: number
  per_page?: number
  is_active?: boolean
  is_published?: boolean
  template_type_id?: string
  search?: string
}

export function getSurveyTemplates(
  params: ListSurveyTemplatesParams = {}
): Promise<PaginatedResponse<SurveyTemplateListItem>> {
  return api.get<PaginatedResponse<SurveyTemplateListItem>>(
    "/survey-templates",
    params as Record<string, unknown>
  )
}

export function getSurveyTemplate(id: string): Promise<SurveyTemplateDetail> {
  return api.get<SurveyTemplateDetail>(`/survey-templates/${id}`)
}

export function createSurveyTemplate(
  data: CreateSurveyTemplateRequest
): Promise<SurveyTemplateDetail> {
  return api.post<SurveyTemplateDetail>("/survey-templates", data)
}

export function updateSurveyTemplate(
  id: string,
  data: UpdateSurveyTemplateRequest
): Promise<SurveyTemplateDetail> {
  return api.put<SurveyTemplateDetail>(`/survey-templates/${id}`, data)
}

export function deleteSurveyTemplate(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/survey-templates/${id}`)
}

/** Modèle ajouté par « Initialiser », avec son envoi automatique. */
export type AddedSurveyTemplate = {
  id: string
  code: string
  name: string
  auto_send_enabled: boolean
  auto_send_trigger: string | null
  auto_send_delay_hours: number | null
}

/** Réponse de « Initialiser » : `added_count` modèles ajoutés. */
export type InitializeSurveyTemplatesResult = {
  message: string
  added_count: number
  added_templates: AddedSurveyTemplate[]
  /** Codes des modèles ajoutés dont l'envoi automatique est resté coupé (organisme qui a des sessions). */
  auto_send_left_off: string[]
}

export function initializeSurveyTemplates(): Promise<InitializeSurveyTemplatesResult> {
  return api.post<InitializeSurveyTemplatesResult>("/survey-templates/initialize")
}

// ===========================================
// Survey Questions
// ===========================================

export function createSurveyQuestion(
  templateId: string,
  data: CreateSurveyQuestionRequest
): Promise<SurveyQuestionDetail> {
  return api.post<SurveyQuestionDetail>(
    `/survey-templates/${templateId}/questions`,
    data
  )
}

export function updateSurveyQuestion(
  questionId: string,
  data: UpdateSurveyQuestionRequest
): Promise<SurveyQuestionDetail> {
  return api.put<SurveyQuestionDetail>(`/survey-questions/${questionId}`, data)
}

export function deleteSurveyQuestion(questionId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/survey-questions/${questionId}`)
}

// ===========================================
// Survey Assignments
// ===========================================

export interface ListSurveyAssignmentsParams {
  page?: number
  per_page?: number
  template_id?: string
  status_code?: string
  session_id?: string
}

export function getSurveyAssignments(
  params: ListSurveyAssignmentsParams = {}
): Promise<PaginatedResponse<SurveyAssignmentListItem>> {
  return api.get<PaginatedResponse<SurveyAssignmentListItem>>(
    "/survey-assignments",
    params as Record<string, unknown>
  )
}

export function createSurveyAssignment(
  data: CreateSurveyAssignmentRequest
): Promise<SurveyAssignmentListItem> {
  return api.post<SurveyAssignmentListItem>("/survey-assignments", data)
}

// ===========================================
// Public Survey (for respondents)
// ===========================================

export function getPublicSurvey(token: string): Promise<PublicSurveyInfo> {
  return api.get<PublicSurveyInfo>(`/surveys/${token}`)
}

export function submitPublicSurvey(
  token: string,
  data: SubmitSurveyRequest
): Promise<{ success: boolean; message: string }> {
  return api.post<{ success: boolean; message: string }>(`/surveys/${token}/submit`, data)
}
